/**
 * Shopify Liquid Quick-View Modal Manager
 *
 * Responsibilities:
 * - Listens for clicks on .hdl-new-purchase__product-title-group and [data-quick-view-trigger]
 * - Fetches product detail page (PDP) HTML via AJAX
 * - Extracts strictly the .pdp-main-product-container element
 * - Injects it into the modal with smooth popup animation and loading spinner
 * - Initializes PDP behaviors (accordions, mobile slider, rating stars, videos, variant picker, and Add-to-Cart)
 * - Supports closing via close button, clicking outside the modal, and the Escape key
 */

(function () {
  'use strict';

  // In-memory cache for fetched PDP containers
  const pdpCache = new Map();

  let modalEl = null;
  let contentEl = null;
  let lastActiveElement = null;
  let currentAbortController = null;

  /**
   * Ensures the modal exists in the DOM and is appended directly to document.body
   */
  function ensureModalInBody() {
    // Deduplicate modal elements if multiple sections include the snippet
    const modals = document.querySelectorAll('#quick-view-modal');
    if (modals.length > 1) {
      for (let i = 1; i < modals.length; i++) {
        modals[i].remove();
      }
    }

    modalEl = document.getElementById('quick-view-modal');
    if (!modalEl) return null;

    if (document.body && modalEl.parentElement !== document.body) {
      document.body.appendChild(modalEl);
    }

    contentEl = document.getElementById('quick-view-modal-content');
    return modalEl;
  }

  /**
   * Initializes modal references and global event handlers
   */
  function init() {
    if (!ensureModalInBody()) return;

    // Close on click (close button, backdrop, or any element with data-quick-view-close)
    modalEl.removeEventListener('click', handleModalClick);
    modalEl.addEventListener('click', handleModalClick);

    // Close on Escape key
    document.removeEventListener('keydown', handleDocumentKeydown);
    document.addEventListener('keydown', handleDocumentKeydown);

    // Click delegation for quick view triggers
    document.removeEventListener('click', handleTriggerClick);
    document.addEventListener('click', handleTriggerClick);

    // Keyboard activation on triggers (Enter / Space)
    document.removeEventListener('keydown', handleTriggerKeydown);
    document.addEventListener('keydown', handleTriggerKeydown);
  }

  /**
   * Handle clicks on Quick View triggers
   */
  function handleTriggerClick(event) {
    const trigger = event.target.closest(
      '.hdl-new-purchase__product-title-group, .hdl-purchase-2__product-title-row, [data-quick-view-trigger]'
    );
    if (!trigger) return;

    // Prevent navigation if an <a> tag was clicked inside the trigger
    event.preventDefault();
    event.stopPropagation();

    const productUrl = resolveProductUrl(trigger);
    if (!productUrl) {
      console.warn('[QuickView] Unable to determine product URL for trigger:', trigger);
      return;
    }

    open(productUrl, trigger);
  }

  /**
   * Handle keyboard Enter/Space on focused triggers
   */
  function handleTriggerKeydown(event) {
    if (event.key !== 'Enter' && event.key !== ' ') return;

    const trigger = event.target.closest(
      '.hdl-new-purchase__product-title-group[role="button"], .hdl-purchase-2__product-title-row[role="button"], [data-quick-view-trigger]'
    );
    if (!trigger || document.activeElement !== trigger) return;

    event.preventDefault();
    trigger.click();
  }

  /**
   * Resolve product URL from trigger or its ancestors/children
   */
  function resolveProductUrl(trigger) {
    // 1. Direct dataset attribute
    if (trigger.dataset.productUrl) {
      return trigger.dataset.productUrl;
    }

    // 2. Child anchor or self anchor
    const anchor = trigger.tagName === 'A' ? trigger : trigger.querySelector('a[href]');
    if (anchor && anchor.getAttribute('href')) {
      return anchor.getAttribute('href');
    }

    // 3. Parent item dataset
    const parentItem = trigger.closest('[data-product-url]');
    if (parentItem && parentItem.dataset.productUrl) {
      return parentItem.dataset.productUrl;
    }

    // 4. Product handle dataset fallback
    const handle = trigger.dataset.productHandle || parentItem?.dataset.productHandle;
    if (handle) {
      return `/products/${handle}`;
    }

    return null;
  }

  /**
   * Open the Quick View modal for a given product URL
   */
  async function open(productUrl, triggerEl) {
    if (!modalEl || !contentEl || modalEl.parentElement !== document.body) {
      ensureModalInBody();
    }
    if (!modalEl || !contentEl) return;

    lastActiveElement = triggerEl || document.activeElement;

    // Show modal & display loading state
    modalEl.classList.add('is-open', 'is-loading');
    modalEl.setAttribute('aria-hidden', 'false');
    document.body.classList.add('quick-view-open');

    // Abort any ongoing fetch
    if (currentAbortController) {
      currentAbortController.abort();
    }
    currentAbortController = new AbortController();

    // Check cache
    if (pdpCache.has(productUrl)) {
      renderContent(pdpCache.get(productUrl), productUrl);
      return;
    }

    try {
      const response = await fetch(productUrl, {
        signal: currentAbortController.signal,
        headers: {
          'X-Requested-With': 'XMLHttpRequest'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      // Requirement: Display only the .pdp-main-product-container section
      const pdpSection = doc.querySelector('.pdp-main-product-container');

      if (!pdpSection) {
        // Fallback if the product detail page uses an alternative section class
        const fallbackContainer = doc.querySelector('.product-section, section.container, .product');
        if (fallbackContainer) {
          filterAllowedElements(fallbackContainer);
          const fallbackHtml = fallbackContainer.outerHTML;
          pdpCache.set(productUrl, fallbackHtml);
          renderContent(fallbackHtml, productUrl);
          return;
        }

        renderError('Product details could not be loaded.', productUrl);
        return;
      }

      // Filter to strictly keep only requested elements:
      // 1. .product__media-wrapper
      // 2. .product__title__wrapper
      // 3. .product__description
      filterAllowedElements(pdpSection);

      const containerHtml = pdpSection.outerHTML;
      pdpCache.set(productUrl, containerHtml);
      renderContent(containerHtml, productUrl);
    } catch (error) {
      if (error.name === 'AbortError') return;
      console.error('[QuickView] Error fetching product:', error);
      renderError('Unable to load product preview at this moment.', productUrl);
    }
  }

  /**
   * Render fetched HTML and initialize interactive behaviors
   */
  function renderContent(html, productUrl) {
    contentEl.innerHTML = html;

    // Execute any inline scripts present inside the extracted section
    executeInlineScripts(contentEl);

    // Initialize all PDP behaviors (accordions, slider, rating, forms)
    initPdpBehaviors(contentEl, productUrl);

    // Hide loader and show content
    modalEl.classList.remove('is-loading');

    // Scroll modal content to top
    contentEl.scrollTop = 0;

    // Focus close button for accessibility
    const closeBtn = modalEl.querySelector('.quick-view-modal__close-btn');
    if (closeBtn) {
      closeBtn.focus();
    }
  }

  /**
   * Render friendly error message inside modal
   */
  function renderError(message, productUrl) {
    modalEl.classList.remove('is-loading');
    contentEl.innerHTML = `
      <div class="quick-view-modal__error">
        <h3 class="quick-view-modal__error-title">Notice</h3>
        <p class="quick-view-modal__error-msg">${message}</p>
        ${
          productUrl
            ? `<a href="${productUrl}" class="quick-view-modal__error-link">View Full Product Page</a>`
            : ''
        }
      </div>
    `;
  }

  /**
   * Close the Quick View modal
   */
  function close() {
    if (!modalEl) return;

    if (currentAbortController) {
      currentAbortController.abort();
      currentAbortController = null;
    }

    modalEl.classList.remove('is-open');
    modalEl.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('quick-view-open');

    // Pause all playing videos in the modal
    if (contentEl) {
      const videos = contentEl.querySelectorAll('video');
      videos.forEach((video) => {
        try {
          video.pause();
        } catch (_) {}
      });
    }

    // Return focus to the trigger
    if (lastActiveElement && typeof lastActiveElement.focus === 'function') {
      lastActiveElement.focus();
    }
  }

  /**
   * Handle modal backdrop and close button clicks
   */
  function handleModalClick(event) {
    if (
      event.target.closest('[data-quick-view-close]') ||
      event.target === modalEl
    ) {
      event.preventDefault();
      close();
    }
  }

  /**
   * Handle Escape key press
   */
  function handleDocumentKeydown(event) {
    if (event.key === 'Escape' || event.keyCode === 27) {
      if (modalEl && modalEl.classList.contains('is-open')) {
        event.preventDefault();
        close();
      }
    }
  }

  /**
   * Filter PDP content to strictly keep only the requested elements:
   * 1. .product__media-wrapper
   * 2. .product__title__wrapper
   * 3. .product__description
   */
  function filterAllowedElements(container) {
    if (!container) return;

    // Allowed content selectors inside product info
    const allowedInfoSelectors = [
      '.product__title__wrapper',
      '.product__description',
      '.quick-view-modal__view-more-wrap',
    ];

    const infoContainer =
      container.querySelector('.product__info-container') ||
      container.querySelector('.product__info-wrapper');

    if (infoContainer) {
      Array.from(infoContainer.children).forEach((child) => {
        const isAllowed = allowedInfoSelectors.some((selector) => {
          return child.matches(selector) || child.querySelector(selector) !== null;
        });

        if (!isAllowed) {
          child.remove();
        }
      });
    }

    // Clean up product row to ensure only media wrapper and info wrapper exist
    const productRow = container.querySelector('.product-all-container');
    if (productRow) {
      Array.from(productRow.children).forEach((child) => {
        const isMedia =
          child.matches('.product__media-wrapper') ||
          child.querySelector('.product__media-wrapper');
        const isInfo =
          child.matches('.product__info-wrapper') ||
          child.querySelector('.product__info-wrapper');

        if (!isMedia && !isInfo) {
          child.remove();
        }
      });
    }

    // In .product__media-wrapper, keep only the first image and remove the rest
    const mediaWrapper = container.querySelector('.product__media-wrapper');
    if (mediaWrapper) {
      // 1. Desktop grid: keep only the first child
      const gridContainer = mediaWrapper.querySelector('.grid-container');
      if (gridContainer) {
        const gridItems = Array.from(gridContainer.children);
        gridItems.slice(1).forEach((item) => item.remove());
      }

      // 2. Mobile gallery: keep only the first slide item
      const mobileList = mediaWrapper.querySelector('.custom-product__media-list, .product__media-list');
      if (mobileList) {
        const mobileItems = Array.from(mobileList.querySelectorAll('.product__media-item'));
        mobileItems.slice(1).forEach((item) => item.remove());
      }

      // 3. Remove mobile thumbs strip and PDP media modal
      const thumbs = mediaWrapper.querySelector('.product-mobile-thumbs, .js-mobile-product-thumbs');
      if (thumbs) {
        thumbs.remove();
      }

      const pdpMediaModal = mediaWrapper.querySelector('#pdp-media-modal, .pdp-media-modal');
      if (pdpMediaModal) {
        pdpMediaModal.remove();
      }
    }
  }

  /**
   * Re-execute inline <script> tags injected via innerHTML
   */
  function executeInlineScripts(container) {
    const scripts = container.querySelectorAll('script');
    scripts.forEach((oldScript) => {
      // Skip JSON and templates
      const type = oldScript.getAttribute('type');
      if (type && type !== 'text/javascript' && type !== 'application/javascript') {
        return;
      }
      const newScript = document.createElement('script');
      Array.from(oldScript.attributes).forEach((attr) => {
        newScript.setAttribute(attr.name, attr.value);
      });
      newScript.textContent = oldScript.textContent;
      oldScript.parentNode.replaceChild(newScript, oldScript);
    });
  }

  /**
   * Replicate PDP behaviors matching sections/pdp-main-product.liquid
   * and assets/pdp-main-product-javascript.js
   */
  function initPdpBehaviors(container, productUrl) {
    // 1. Mobile Gallery & Slick Slider
    initMobileGallery(container);

    // 2. Collapsible Content Accordions
    initCollapsibleContent(container);

    // 3. Rating Stars
    initRatingStars(container);

    // 4. Clinical Proofs & Stats
    initPdpProofs(container);

    // 5. View More Button below .pdp-proofs
    initViewMoreButton(container, productUrl);

    // 6. Video Playback & Sound Toggles
    initVideos(container);

    // 7. Variant Selectors & Radio Buttons
    initVariantPicker(container);

    // 8. Add-to-Cart Form Submission & Cart Drawer Integration
    initProductForm(container);
  }

  /**
   * 1. Mobile Product Gallery Slick Slider
   */
  function initMobileGallery(container) {
    if (typeof window.jQuery === 'undefined' || typeof window.jQuery.fn.slick === 'undefined') {
      return;
    }

    const $ = window.jQuery;
    const mobileWrappers = container.querySelectorAll('.product-mobile');

    mobileWrappers.forEach((wrapper) => {
      const items = wrapper.querySelectorAll('.product__media-item');
      if (items.length <= 1) return;

      const $main = $(wrapper).find('.js-mobile-product-gallery').not('[data-featured-only="true"]');
      const thumbs = wrapper.querySelector('.js-mobile-product-thumbs');

      if (!$main.length) return;

      if (!$main.hasClass('slick-initialized')) {
        $main.slick({
          dots: false,
          arrows: false,
          draggable: true,
          swipe: true,
          touchMove: true,
          infinite: false,
          initialSlide: 0,
          adaptiveHeight: false,
          speed: 250,
        });
      } else {
        $main.slick('setPosition');
      }

      if (!thumbs) return;

      const thumbButtons = thumbs.querySelectorAll('.product-mobile-thumbs__item');

      const scrollThumbIntoStrip = (btn) => {
        if (!btn || !thumbs) return;
        const left = btn.offsetLeft - (thumbs.clientWidth - btn.offsetWidth) / 2;
        thumbs.scrollTo({
          left: Math.max(0, left),
          behavior: 'smooth',
        });
      };

      const setActiveThumb = (index) => {
        thumbButtons.forEach((btn) => {
          const isActive = Number(btn.dataset.slideIndex) === index;
          btn.classList.toggle('is-active', isActive);
          if (isActive) scrollThumbIntoStrip(btn);
        });
      };

      thumbButtons.forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          const index = Number(btn.dataset.slideIndex) || 0;
          $main.slick('slickGoTo', index);
          setActiveThumb(index);
        });
      });

      $main.off('afterChange.quickViewThumbs').on('afterChange.quickViewThumbs', (_e, _slick, currentSlide) => {
        setActiveThumb(currentSlide);
      });
    });
  }

  /**
   * 2. Collapsible Content Accordions
   */
  function initCollapsibleContent(container) {
    const toggleButtons = container.querySelectorAll('.collapsible-content__toggle');
    const answerContents = container.querySelectorAll('.collapsible-content__answer');

    toggleButtons.forEach((toggleButton, index) => {
      toggleButton.addEventListener('click', (e) => {
        e.preventDefault();

        // Close all other accordions
        answerContents.forEach((answerContent, i) => {
          if (i === index) return;
          answerContent.classList.remove('active');

          const otherPlus = toggleButtons[i]?.querySelector('.plus');
          const otherMinus = toggleButtons[i]?.querySelector('.minus');
          if (otherPlus) otherPlus.style.display = 'block';
          if (otherMinus) otherMinus.style.display = 'none';
        });

        // Toggle current accordion
        if (answerContents[index]) {
          const isNowActive = answerContents[index].classList.toggle('active');

          const plusIcon = toggleButton.querySelector('.plus');
          const minusIcon = toggleButton.querySelector('.minus');

          if (plusIcon) plusIcon.style.display = isNowActive ? 'none' : 'block';
          if (minusIcon) minusIcon.style.display = isNowActive ? 'block' : 'none';
        }
      });
    });
  }

  /**
   * 3. Rating Stars Fill
   */
  function initRatingStars(container) {
    const starInners = container.querySelectorAll('.ratings-stars .star-inner');
    starInners.forEach((star) => {
      star.style.width = `${(4.6 / 5) * 100}%`;
    });
  }

  /**
   * 4. Clinical Proofs / Stats
   */
  function initPdpProofs(container) {
    const proofs = container.querySelector('.pdp-proofs');
    if (!proofs) return;

    const statsContainers = proofs.querySelectorAll('.pdp-proofs-stats');
    if (statsContainers.length === 1) {
      proofs.classList.add('single');
      statsContainers[0].style.display = 'flex';
      return;
    }
    if (statsContainers.length >= 2) {
      statsContainers.forEach((el, idx) => {
        el.classList.toggle('active', idx === 0);
      });
    }
  }

  /**
   * 5. View More Button below .pdp-proofs
   */
  function initViewMoreButton(container, productUrl) {
    if (!productUrl) return;

    if (container.querySelector('.quick-view-modal__view-more-wrap')) return;

    const pdpProofs = container.querySelector('.pdp-proofs');
    const infoContainer =
      container.querySelector('.product__info-container') ||
      container.querySelector('.product__info-wrapper');

    const isGerman =
      window.location.pathname.includes('/de') ||
      (document.documentElement.lang && document.documentElement.lang.toLowerCase().startsWith('de'));
    const btnText = isGerman ? 'Mehr erfahren' : 'View More';

    const wrap = document.createElement('div');
    wrap.className = 'quick-view-modal__view-more-wrap';
    wrap.innerHTML = `
      <a href="${productUrl}" class="quick-view-modal__view-more-btn" aria-label="${btnText}">
        <span>${btnText}</span>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <line x1="5" y1="12" x2="19" y2="12"></line>
          <polyline points="12 5 19 12 12 19"></polyline>
        </svg>
      </a>
    `;

    if (pdpProofs && pdpProofs.parentNode) {
      pdpProofs.insertAdjacentElement('afterend', wrap);
    } else if (infoContainer) {
      infoContainer.appendChild(wrap);
    }
  }

  /**
   * 6. Videos & Mute Buttons
   */
  function initVideos(container) {
    const videos = container.querySelectorAll('.product-video, .product-ugc-video');
    const muteButtons = container.querySelectorAll('.sound-button, .mute-unmute-button');

    videos.forEach((video) => {
      video.muted = true;
      if (video.autoplay) {
        video.play().catch(() => {});
      }
    });

    muteButtons.forEach((btn, index) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const video = videos[index] || btn.closest('.half-width, .product__media-item')?.querySelector('video');
        if (!video) return;

        video.muted = !video.muted;

        const muteIcon = btn.querySelector('.mute-icon, .mute-mobile-icon');
        const unmuteIcon = btn.querySelector('.unmute-icon, .unmute-mobile-icon');

        if (video.muted) {
          if (muteIcon) muteIcon.style.display = 'block';
          if (unmuteIcon) unmuteIcon.style.display = 'none';
        } else {
          if (muteIcon) muteIcon.style.display = 'none';
          if (unmuteIcon) unmuteIcon.style.display = 'block';
          video.play().catch(() => {});
        }
      });
    });
  }

  /**
   * 5. Variant Picker Radio / Dropdown handlers
   */
  function initVariantPicker(container) {
    const radioInputs = container.querySelectorAll('input[type="radio"].collapsible-radio, input[name="id"]');
    const formIdInput = container.querySelector('form input[name="id"]');

    radioInputs.forEach((radio) => {
      radio.addEventListener('change', () => {
        if (radio.name !== 'id' && radio.dataset.variantId && formIdInput) {
          formIdInput.value = radio.dataset.variantId;
        }

        // Highlight collapsible parameters if present
        container.querySelectorAll('.product-parameters .collapsible-content__item').forEach((item) => {
          item.classList.toggle('active', item.contains(radio) && radio.checked);
        });
      });
    });
  }

  /**
   * 6. Product Form & Add to Cart Handling
   */
  function initProductForm(container) {
    const forms = container.querySelectorAll('form[action*="/cart/add"], form[data-type="add-to-cart-form"]');

    forms.forEach((form) => {
      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        e.stopPropagation();

        const submitBtn = form.querySelector('[type="submit"], .add-to-cart-button, .product-form__submit');
        if (submitBtn && submitBtn.classList.contains('loading')) return;

        if (submitBtn) {
          submitBtn.classList.add('loading');
          submitBtn.setAttribute('aria-disabled', 'true');
        }

        try {
          const formData = new FormData(form);

          // If cart drawer is present in theme, request updated cart sections
          const cartDrawer = document.querySelector('cart-drawer');
          if (cartDrawer && typeof cartDrawer.getSectionsToRender === 'function') {
            const sectionIds = cartDrawer.getSectionsToRender().map((s) => s.id);
            formData.append('sections', sectionIds.join(','));
            formData.append('sections_url', window.location.pathname);
          }

          const response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: {
              'X-Requested-With': 'XMLHttpRequest',
              Accept: 'application/javascript, application/json',
            },
            body: formData,
          });

          const data = await response.json();

          // Dispatch theme event notifications
          if (window.jQuery) {
            window.jQuery(document).trigger('product-form-responsed', [data]);
          }
          document.dispatchEvent(new CustomEvent('cart:updated', { detail: { item: data } }));
          document.dispatchEvent(new CustomEvent('product-form-responsed', { detail: data }));

          if (data.status && data.status >= 400) {
            // Cart error handling
            const errorWrapper = form.querySelector('.product-form__error-message-wrapper');
            const errorMsg = form.querySelector('.product-form__error-message');
            if (errorWrapper && errorMsg) {
              errorMsg.textContent = data.description || data.message || 'Error adding to cart';
              errorWrapper.removeAttribute('hidden');
            }
            return;
          }

          // Cart Drawer integration
          if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
            cartDrawer.renderContents(data);
            if (typeof cartDrawer.open === 'function') {
              close();
              cartDrawer.open();
              return;
            }
          }

          // Fallback UI feedback if cart drawer is not active
          if (submitBtn) {
            const originalText = submitBtn.innerHTML;
            submitBtn.innerHTML = '<span>✓ Added to cart</span>';
            setTimeout(() => {
              submitBtn.innerHTML = originalText;
            }, 2200);
          }
        } catch (err) {
          console.error('[QuickView] Error submitting product form:', err);
        } finally {
          if (submitBtn) {
            submitBtn.classList.remove('loading');
            submitBtn.removeAttribute('aria-disabled');
          }
        }
      });
    });
  }

  // Self-initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Shopify Theme Editor Section support
  document.addEventListener('shopify:section:load', () => {
    init();
  });

  // Expose global controller for programmatic access if needed
  window.VedicQuickView = {
    open,
    close,
    clearCache: () => pdpCache.clear(),
  };
})();

